// What a job is WORTH, against what Job Costs says it cost.
//
// Pure arithmetic on plain objects: no DOM, no network, no Supabase. The admin
// tab feeds it rows and reads numbers back, which is what makes every branch
// here testable without a browser.
//
// A contract line earns one of two ways, chosen per line because the billing
// model changes by trade and by contract — a mason bills by block laid, a
// drywaller by SF, an electrician usually lump-sum by floor, and most jobs mix.

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

/**
 * Resolve the sell rate per material type, the one place this happens so the
 * two screens that price a quantity line (this tab and Job Costs' margin
 * line) cannot drift again.
 *
 * A per-project override in project_material_rates wins where one exists —
 * Trade Mark has legacy rows here and they must keep pricing what they
 * always priced. Everywhere else, the org-wide default is
 * material_types.default_revenue_rate: the column Rates & Defaults actually
 * writes to. project_material_rates otherwise has no live writer, so reading
 * ONLY that table (the bug this replaces) left every other org unpriceable
 * forever, no matter what the admin typed.
 *
 * Null when neither is set — never a silent zero.
 */
export function buildRateMap(projectRateRows, materialTypeRows) {
  const rates = {};
  (materialTypeRows || []).forEach(t => {
    if (t.default_revenue_rate != null) rates[t.id] = Number(t.default_revenue_rate);
  });
  (projectRateRows || []).forEach(r => {
    if (r.revenue_rate != null) rates[r.material_type_id] = Number(r.revenue_rate);
  });
  return rates;
}

/**
 * OPEN LINE — the owner's approved rule for a trade/contract shape that has
 * no agreed cap, only an agreed unit price (T&M, board-count drywall, a
 * scope nobody bothered to estimate a quantity for): a plain quantity base
 * line (never a continuation — continues_line_id always wins, see below)
 * whose estimated_qty is NULL, blank, or 0. It earns every unit the field
 * installs × its rate, forever, uncapped — the mirror image of an ordinary
 * quantity line's "never past 100% of its own scope" rule.
 *
 * estimated_qty === 0 counts as open, not "zero scope earning nothing": the
 * add-line modal never let a capped line save with 0 before this feature
 * (jvSaveLine required > 0), so a stored 0 is unreachable except as "the
 * admin left it blank" — treating it identically to NULL means a stray 0
 * behaves the same as the blank state the UI actually produces, instead of
 * silently earning nothing forever.
 *
 * A line with continues_line_id set is NEVER open, even if it happens to
 * carry earn_method 'quantity' and no estimated_qty — a continuation earns
 * off its ROOT's rate/cap chain entirely (see lineEarned's first branch),
 * so "open" is not a question that applies to it.
 */
export function isOpenLine(line) {
  if (!line) return false;
  return line.earn_method === 'quantity' && !line.continues_line_id && !(num(line.estimated_qty) > 0);
}

/**
 * Sum installed quantity per material type from production_line_items rows.
 * A row with no material_type_id (free-text/legacy production that isn't
 * tied to a billable item) cannot earn anything and is ignored, the same way
 * both callers already treated it before this was shared.
 */
export function buildInstalledMap(productionLineRows) {
  const installed = {};
  (productionLineRows || []).forEach(r => {
    if (!r.material_type_id) return;
    installed[r.material_type_id] = (installed[r.material_type_id] || 0) + Number(r.quantity || 0);
  });
  return installed;
}

/**
 * What this line is worth in full.
 *
 * A line that CONTINUES another (continues_line_id set) is always priced at
 * its own typed contract_amount, regardless of earn_method — the owner's
 * rule that an approved change order "stays MONETARILY separate" (the GC's
 * schedule of values lists it at its own dollar figure, e.g. "CO 001 —
 * $4,742") applies to the CONTRACT VALUE unconditionally, even though its
 * EARNED figure below is computed automatically from installed overflow
 * rather than typed. In practice a continuation is always earn_method
 * 'percent' (the reconciler only ever creates CO/EWO lines that way), so
 * this branch and the plain percent branch agree — but checking
 * continues_line_id first keeps that true even if a future line breaks that
 * pattern.
 */
export function lineContract(line, rate) {
  if (!line) return 0;
  if (line.continues_line_id) return num(line.contract_amount);
  if (line.earn_method === 'quantity') {
    // An OPEN line (see isOpenLine) has no agreed total by design — there is
    // no estimated_qty to multiply by a rate, and contract_amount is never
    // written for a quantity line (open or capped) in the first place. This
    // returns 0 unconditionally, rate or no rate: the P&L's Contract column
    // simply does not count an open line, the same way a truly unpriced
    // line doesn't. The UI shows "Open" here instead of a misleading
    // "$0.00" (see 68-jobvalue.js) — 0 is the right NUMBER, just not the
    // right WORDS on screen.
    if (isOpenLine(line)) return 0;
    if (rate == null) return 0;          // no sell price on file yet
    return num(line.estimated_qty) * num(rate);
  }
  return num(line.contract_amount);
}

/**
 * What has been earned on this line so far.
 *
 * Three cases, checked in this order:
 *
 * 1. A line that CONTINUES another (continues_line_id set) — a change order
 *    that is "more of the same item" rather than its own scope. Its typed
 *    percent_complete is IGNORED entirely: progress is automatic. `rate` is
 *    the ROOT base line's sell rate (the caller resolves this — see
 *    allocateInstalled/projectValue, since a CO line usually has no
 *    material_type_id of its own to look a rate up by), and `installedQty`
 *    is this line's own ALLOCATED share of the installed overflow (also the
 *    caller's job — see allocateInstalled, which is the only place that
 *    knows how much of the overflow earlier, earlier-created continuations
 *    on the same chain already claimed). The cap is derived here again from
 *    contract_amount ÷ rate — redundant with what allocateInstalled already
 *    enforced when it computed installedQty, but cheap insurance against a
 *    caller that (for a single, unchained CO) passes the raw shared
 *    installed figure instead of running the allocator first.
 *    rate null/0 is the unpriceable-chain guard: without a $/unit rate there
 *    is no way to say how many dollars of overflow were installed, so this
 *    reads as 0 (and linePercent below reads as null, not a false 0%).
 *    contract_amount <= 0 is guarded too: a DEDUCTIVE change order (a
 *    negative amount, backing scope OUT of the contract) is not an overflow
 *    drinker — without this guard, impliedCap clamps to 0 (Math.max(0, ...)
 *    on a negative ÷ rate), capped becomes 0, but
 *    `Math.min(0 * r, contract_amount)` then picks contract_amount itself
 *    because it is the smaller (more negative) of the two — instantly
 *    "earning" the CO's full negative amount regardless of installed
 *    quantity. Returning 0 up front for contract_amount <= 0 avoids that
 *    Math.min trap entirely; the UI also hides the Continues control on
 *    such a line (see 68-jobvalue.js) since a deduction has no "overflow"
 *    to earn against.
 *
 * 2. An ordinary quantity line (no continues_line_id): capped at
 *    estimated_qty × rate. The owner's rule — a base line's schedule-of-
 *    values entry never earns past 100% of its own scope. Installing more
 *    than estimated is a signal ("more work than the estimate"), not extra
 *    revenue on this line; see allocateInstalled for where that overflow is
 *    actually accounted (a linked CO, or unapproved overage).
 *
 *    UNLESS the line is OPEN (isOpenLine — no estimated_qty on file): then
 *    there is no cap to speak of, by design, and this returns
 *    installedQty × rate uncapped, no matter how large. Deliberately NOT
 *    gated by lineContract/contract_amount the way the continuation branch
 *    above is — an open line's contract is 0 by definition (see
 *    lineContract), and a naive `if (contract <= 0) return 0` guard here
 *    (mirroring the continuation branch's deductive-CO guard) would zero out
 *    every open line's real, earned dollars. There is nothing to guard: this
 *    branch never reads contract_amount at all, open or capped.
 *
 * 3. A plain percent line: unchanged, contract_amount × percent_complete.
 */
export function lineEarned(line, rate, installedQty) {
  if (!line) return 0;
  if (line.continues_line_id) {
    if (num(line.contract_amount) <= 0) return 0;   // deductive CO — nothing to drink into
    const r = num(rate);
    if (rate == null || r === 0) return 0;
    const impliedCap = Math.max(0, num(line.contract_amount) / r);
    const capped = Math.min(Math.max(0, num(installedQty)), impliedCap);
    return Math.min(capped * r, num(line.contract_amount));
  }
  if (line.earn_method === 'quantity') {
    if (rate == null) return 0;
    if (isOpenLine(line)) return Math.max(0, num(installedQty)) * num(rate);   // uncapped, on purpose
    const capped = Math.min(Math.max(0, num(installedQty)), num(line.estimated_qty));
    return capped * num(rate);
  }
  return num(line.contract_amount) * num(line.percent_complete) / 100;
}

/**
 * How far along this line is, 0-100+ for a plain percent line (still
 * capped at 100 by its own input validation elsewhere), never past 100 for
 * a quantity line or a continuation (see lineEarned), or null when the
 * question has no answer (no rate on file — either an ordinary quantity
 * line or a continuation whose chain is unpriceable — or a line worth
 * nothing). Null renders as "—"; zero would be a lie.
 *
 * An OPEN line (isOpenLine) always lands here too, even while it is
 * genuinely earning real money: lineContract returns 0 for it (no agreed
 * total), so `contract <= 0` below is true and this returns null — no code
 * path specific to "open" needed. The UI reads this null as "Open" rather
 * than "—" (see 68-jobvalue.js), the one place the two null-percent cases
 * (open vs. simply worth nothing) are told apart, since only the UI has the
 * line shape on hand to ask isOpenLine itself.
 */
export function linePercent(line, rate, installedQty) {
  if (!line) return null;
  if (rate == null && (line.earn_method === 'quantity' || line.continues_line_id)) return null;
  const contract = lineContract(line, rate);
  if (contract <= 0) return null;
  return lineEarned(line, rate, installedQty) / contract * 100;
}

/**
 * Walk a continues_line_id chain up to its root — the base quantity line
 * that a CO ultimately continues. Today the UI only ever lets a CO/EWO line
 * link DIRECTLY to a base line (see 68-jobvalue.js's Continues control,
 * which only lists quantity lines), so in practice this resolves in one
 * hop. Walking anyway (rather than assuming one hop) means a future data
 * shape — or a manually-edited row — that chains a CO to another CO still
 * resolves to the real base instead of silently mispricing. hops guards
 * against a cycle a corrupted row could otherwise spin forever on; the
 * no-self-continue CHECK constraint prevents the trivial case but not a
 * longer cycle typed directly into the database.
 *
 * Returns null for a dangling link (continues_line_id pointing at a line
 * not in this render's `lines` — the DB's ON DELETE SET NULL means this
 * shouldn't outlive one query, but a caller must not crash on stale local
 * data either) or a line that fails to resolve to a genuine base line.
 */
function _resolveRootLine(line, byId, hops) {
  if (!line) return null;
  if (!line.continues_line_id) return line;
  if ((hops || 0) > 50) return null;
  const next = byId[line.continues_line_id];
  if (!next) return null;
  return _resolveRootLine(next, byId, (hops || 0) + 1);
}

// created_at, then id as the final tiebreak. Two change orders inserted by
// the same jvReconcileChangeOrders() pass (one INSERT with multiple rows)
// can share the exact same created_at timestamp — the database gives no
// guarantee about the order it returns those rows in, so without an id
// tiebreak, which CO drinks the overflow first could silently flip between
// repaints of the same tab. id (a uuid) has no ordering MEANING, but it is
// stable — the point is only that repeated sorts of the same rows always
// agree with each other and with themselves.
const _createdAtCmp = (a, b) =>
  (new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()) ||
  String(a.id || '').localeCompare(String(b.id || ''));

// The order base lines of ONE material type drink installed quantity in:
// capped before open, then sort_order, then created_at/id. One comparator so
// the allocator and installedShownByLine() below can never disagree about
// which line is "last".
const _baseDrainCmp = (a, b) => (isOpenLine(a) ? 1 : 0) - (isOpenLine(b) ? 1 : 0) ||
  (num(a.sort_order) - num(b.sort_order)) || _createdAtCmp(a, b);

// Quantities in and out of here can carry rate-division noise (rates store
// up to 8 decimals) — round the numbers this function hands back so a
// dust-sized float (49.99999999999994) never reads as real overage.
const _roundQty = (v) => Math.round(v * 1e6) / 1e6;
// Money to the cent — same rounding as roundMoney below, for values already
// known to be finite. Half AWAY from zero on both sides: Math.round alone
// rounds −x.5 toward zero and +x.5 away from it, so a change order and its
// exact reversal at the same % (+1,081.625 / −1,081.625) left a cent earned.
// "+ 0" turns a negative zero (a float crumb rounded away) into plain 0.
const _cents = (v) => Math.sign(v) * Math.round(Math.abs(v) * 100) / 100 + 0;

/**
 * Drain installed quantity through a material type's base line(s) first,
 * then through every change order that CONTINUES one of them, in the order
 * each was approved (created_at) — never array order, since the reconciler
 * and a repaint can hand lines back in any order.
 *
 * Returns `{ byLineId, overageByType, typeByLineId }`:
 *   - typeByLineId[line.id]  which PRODUCT each quantity line belongs to — its
 *                           own material_type_id for a base line, its root's
 *                           for a continuation. Recorded here rather than
 *                           re-derived by callers because resolving a
 *                           continuation's product means walking
 *                           continues_line_id, which this function already
 *                           does; a second, simpler lookup elsewhere would
 *                           silently miss it. A line with no material type
 *                           gets no entry at all.
 *   - byLineId[line.id]     the quantity THIS line absorbed — a base line's
 *                           own installed progress (capped at its
 *                           estimated_qty) or a continuation's share of the
 *                           overflow (capped at contract_amount ÷ the root's
 *                           rate).
 *   - overageByType[matId]  whatever installed quantity is left after the
 *                           base and every linked continuation are full —
 *                           unapproved overage. Earns nothing anywhere.
 *                           A type with nothing left over has no key here
 *                           at all (never a 0), so callers can test
 *                           presence instead of value.
 *
 * A continuation whose chain's rate is null/0 is unpriceable: its cap
 * cannot be expressed in quantity terms (no $/unit to divide by), so it
 * absorbs nothing and whatever it would have caught instead counts as
 * overage. The base line ahead of it still absorbs normally — a missing
 * price does not change how much was physically installed, only whether
 * any of it earns money (see lineEarned).
 *
 * OPEN LINES (isOpenLine — a base quantity line with no estimated_qty):
 * within one material type's base lines, every CAPPED base drinks to its
 * own cap FIRST, in the existing sort_order/created_at order, exactly as
 * before this feature — an open line never jumps the queue ahead of a
 * capped one just because of where it sits in that order. Only once every
 * capped base of that type is full does an open base take its turn, and
 * when it does it takes EVERYTHING still remaining, uncapped — there is no
 * "cap" value to compute for it at all. Two base lines sharing a material,
 * one capped one open, is therefore fully deterministic regardless of which
 * one the admin typed first. A material type with an open base line can
 * never produce an overageByType entry: the open line is the last base to
 * run and always drains `remaining` to exactly 0.
 *
 * A continues_line_id whose chain resolves to an OPEN root is dangling, not
 * a working continuation — an open base already drinks every unit itself,
 * so there is never any overflow left for a linked CO to earn against (see
 * isOpenLine's own doc for why the UI stops this link from being created
 * going forward). Skipped here the same way a link to a deleted line is
 * skipped, so such a CO is left out of continuationsByType entirely —
 * projectValue below independently treats its root as unpriced too, so its
 * earned reads 0 and its percent reads null ("—"), never a false 0%.
 */
export function allocateInstalled(lines, ratesByMaterialType, installedByMaterialType) {
  const rates = ratesByMaterialType || {};
  const installed = installedByMaterialType || {};
  const allLines = lines || [];
  const byId = {};
  allLines.forEach(l => { if (l && l.id != null) byId[l.id] = l; });

  const basesByType = {};
  const typeByLineId = {};
  allLines.forEach(l => {
    if (l && l.earn_method === 'quantity' && !l.continues_line_id) {
      const t = l.material_type_id;
      (basesByType[t] = basesByType[t] || []).push(l);
      // The REAL value, not the object key `t` — a null material type keys as
      // the string 'null', and a caller matching this map against a genuine
      // installed-quantity map must never collide on that.
      if (t != null) typeByLineId[l.id] = t;
    }
  });
  // Capped lines always sort BEFORE open lines within the same material type
  // — the open line's "take everything left" only makes sense as the LAST
  // base to run. Within each of those two groups, the existing tiebreak
  // (sort_order, then created_at) is unchanged.
  Object.values(basesByType).forEach(arr => arr.sort(_baseDrainCmp));

  const continuationsByType = {};
  allLines.forEach(l => {
    if (!l || !l.continues_line_id) return;
    const root = _resolveRootLine(l, byId, 0);
    // dangling/invalid link — nothing to inherit, so nothing to record.
    if (!root || root.earn_method !== 'quantity' || root.continues_line_id) return;
    // WHICH PRODUCT this line belongs to is recorded before the open-root
    // guard below, because the two questions are different: an open root
    // drinks every installed unit itself, so a CO linked to it can never
    // ALLOCATE quantity — but the money filed against that CO still belongs
    // to the root's product, and a cost-per-unit view has to see it.
    if (root.material_type_id != null) typeByLineId[l.id] = root.material_type_id;
    if (isOpenLine(root)) return;                    // see doc above
    const t = root.material_type_id;
    (continuationsByType[t] = continuationsByType[t] || []).push(l);
  });
  Object.values(continuationsByType).forEach(arr => arr.sort(_createdAtCmp));

  const byLineId = {};
  const overageByType = {};
  const types = new Set([...Object.keys(basesByType), ...Object.keys(installed)]);

  types.forEach(type => {
    let remaining = Math.max(0, num(installed[type]));

    (basesByType[type] || []).forEach(line => {
      // OPEN: no cap — drinks whatever is left, however much that is. Sorted
      // last within this type's bases (see the sort above), so every capped
      // base has already taken its own share by the time this runs.
      if (isOpenLine(line)) {
        byLineId[line.id] = _roundQty(remaining);
        remaining = 0;
        return;
      }
      const cap = Math.max(0, num(line.estimated_qty));
      const take = Math.min(cap, remaining);
      byLineId[line.id] = _roundQty(take);
      remaining -= take;
    });

    const rate = rates[type];
    (continuationsByType[type] || []).forEach(line => {
      if (rate == null || num(rate) === 0) {
        byLineId[line.id] = 0;           // unpriceable — see doc comment above
        return;
      }
      const cap = Math.max(0, num(line.contract_amount) / num(rate));
      const take = Math.min(cap, remaining);
      byLineId[line.id] = _roundQty(take);
      remaining -= take;
    });

    if (remaining > 1e-9) overageByType[type] = _roundQty(remaining);
  });

  return { byLineId, overageByType, typeByLineId };
}

/**
 * Whether a line counts as finished on day `asOf` ('YYYY-MM-DD'): its
 * finished_on is on or before it. No asOf, or no finished_on, is never
 * finished — Reopen (finished_on NULL) is exactly the line it was before.
 */
export function finishedBy(line, asOf) {
  return !!(line && line.finished_on && asOf && line.finished_on <= asOf);
}

/**
 * Roll the lines up into the figures the tab shows. `perLine` carries each
 * line's own contract/earned/percent so the Contract hero can draw a
 * per-line breakdown (mirroring Job Costs' category bars) without a second,
 * separate walk over `lines` in the render layer that could compute the
 * same numbers slightly differently.
 *
 * Runs allocateInstalled() once and returns its result as `alloc` — the raw
 * { byLineId, overageByType } — so the render layer's row-by-row detail
 * (a continuation's "X of Y installed" progress, a base line's "N beyond
 * scope" warning, which CO absorbed a base line's overflow) reads the exact
 * numbers this total was built from instead of a second walk that could
 * drift out of sync. 68-jobvalue.js is the only consumer; job-value.js
 * itself only needs byLineId/overageByType to resolve each line's own
 * rate/installedQty below.
 *
 * FINISHED (owner, 2026-10-05: "if I finish the task, the remainder must go in
 * as profit"). `asOf` ('YYYY-MM-DD') is the day these figures stand on — job
 * to date passes today, a period run its own as-of day. A line finished by then
 * (finishedBy) that has a contract value — a fixed amount, a capped quantity
 * line or a continuation, priced, worth more than 0 — earns its WHOLE contract
 * from its finished_on day on, whatever its progress says. Every screen reads
 * this result, so the Contract tab, Job Costs, the P&L (the remainder lands in
 * the period holding finished_on, like a final invoice), its PDF, Home and the
 * exports all move together. Nothing else changes: an open, unpriced or credit
 * line earns as it always did (but a deduction continuing a finished root
 * counts in full — see below), allocateInstalled is not touched (finishing
 * invents no overflow and does not fill a continuation), and with no asOf, no
 * finished line or a finish date still ahead every figure is what it was.
 * perLine carries `finished: true` on a line the rule holds, with
 * `progressEarned` — what its progress alone earns — so a screen can say both.
 */
export function projectValue(lines, ratesByMaterialType, installedByMaterialType, asOf) {
  const rates = ratesByMaterialType || {};
  const installed = installedByMaterialType || {};
  const allLines = lines || [];
  const byId = {};
  allLines.forEach(l => { if (l && l.id != null) byId[l.id] = l; });
  const alloc = allocateInstalled(allLines, rates, installed);

  const unpriced = [];
  const perLine = [];
  let contract = 0, earned = 0, baseContract = 0, extrasContract = 0, openEarned = 0;

  for (const line of allLines) {
    // A continuation prices off the ROOT base line's rate (it usually has no
    // material_type_id of its own) and earns against its OWN allocated share
    // of the overflow (allocateInstalled already worked out how much earlier
    // continuations on the same chain claimed first) — never the raw shared
    // installed figure, which lineEarned's own capping alone cannot
    // disambiguate between two chained COs.
    let rate, installedQty, unpricedLine = false, rootWhole = false;
    if (line.continues_line_id) {
      const root = _resolveRootLine(line, byId, 0);
      // A root that resolves to an OPEN line is dangling, same family as a
      // root that doesn't resolve at all (see allocateInstalled's own doc):
      // an open base drinks every unit itself, so there is never overflow
      // left for this "continuation" to earn against. Forcing rate to null
      // here — rather than resolving the root's real rate — is what makes
      // this line read as unpriced/"—" everywhere downstream (lineEarned's
      // `rate == null` guard, linePercent's null, the unpriced list below)
      // instead of a technically-truthful-but-meaningless "0%" that looks
      // like a working control.
      const rootUsable = root && !isOpenLine(root);
      rate = rootUsable ? (rates[root.material_type_id] ?? null) : null;
      installedQty = alloc.byLineId[line.id] ?? 0;
      unpricedLine = rate == null;
      // Its root earns its whole contract by asOf (the FINISHED rule below).
      rootWhole = !unpricedLine && _cents(lineContract(root, rate)) > 0 && finishedBy(root, asOf);
    } else if (line.earn_method === 'quantity') {
      rate = rates[line.material_type_id] ?? null;
      // The allocator's OWN split for this line, never the raw shared
      // installed figure for the material type. Two base lines sharing one
      // material type (rare, but the schema allows it — e.g. a scope typed
      // as two lines) would otherwise each cap independently against the
      // SAME shared total, double-counting: est 1,000 + est 800, installed
      // 1,200, would earn as if 1,800 were installed. allocateInstalled
      // already drained the shared installed figure across every base line
      // of this type in order, so alloc.byLineId[line.id] is this line's
      // true share — and for the single-base case it is PROVABLY the exact
      // same number the old `installed[type]` expression produced once
      // capped: allocateInstalled's own cap is `min(estimated_qty,
      // remaining)`, remaining is the untouched raw installed figure when
      // there is nothing ahead of it in the drain order, and lineEarned
      // caps at estimated_qty × rate regardless — the two capping passes
      // agree because they are the same cap.
      installedQty = alloc.byLineId[line.id] ?? 0;
      unpricedLine = rate == null;
    } else {
      rate = null;
      installedQty = 0;
    }
    if (unpricedLine) unpriced.push(line);

    // WHOLE CENTS, PER LINE, HERE — the one place every screen's contract and
    // earned figure comes from. A quantity line is quantity × a rate stored to
    // 8 decimals and a percent line is amount × a typed percent, so neither is
    // a whole number of cents; left raw, each screen rounded its own copy and
    // a row could sit a cent away from the same line on another tab, the rows
    // a cent away from their own total, and an export carried the float tail.
    // Rounded once here, the line IS its printed figure and every total below
    // is a sum of printed figures — it re-adds on a calculator, on every tab.
    // linePercent keeps the unrounded ratio: a percent is not money.
    const c = _cents(lineContract(line, rate));
    const progressEarned = _cents(lineEarned(line, rate, installedQty));
    // Finished by asOf with a contract value: the whole contract (see above).
    // A deduction (a negative change order) continuing a root that does gives
    // back its units in full too — the root's whole contract still counts
    // them, and lineEarned has a deduction earn 0 — or the job would earn
    // more than its contract.
    const finished = c > 0 && !unpricedLine && finishedBy(line, asOf);
    const e = finished || (c < 0 && rootWhole) ? c : progressEarned;
    const p = finished ? 100 : linePercent(line, rate, installedQty);
    const open = isOpenLine(line);
    contract += c;
    earned   += e;
    if (open) openEarned += e;
    if (line.source === 'base') baseContract += c; else extrasContract += c;
    // `open` — carried once here rather than re-derived by every consumer —
    // is what lets the render layer (68-jobvalue.js, 66-jobcosts.js) tell
    // "no % because nothing priced" apart from "no % because this line has
    // no cap by design", both of which land on the identical percent: null.
    perLine.push({ id: line.id, description: line.description, source: line.source,
                   contract: c, earned: e, percent: p, open,
                   // Only on a line the finished rule holds — every other line
                   // reads exactly as it did before the rule existed.
                   ...(finished ? { finished, progressEarned } : {}) });
  }

  // Sums of whole cents — this only strips the float noise an addition leaves
  // (0.1 + 0.2), it never moves a figure.
  contract = _cents(contract); earned = _cents(earned); openEarned = _cents(openEarned);
  baseContract = _cents(baseContract); extrasContract = _cents(extrasContract);

  return {
    contract, earned,
    // "Left to earn" only makes sense against the CAPPED/PERCENT portion of
    // the job — an open line has no cap, so it never has anything "left" to
    // earn, only more that COULD be earned. Naively `contract - earned`
    // would go NEGATIVE the moment an open line earns anything at all
    // (its own contract is always 0, but its earned is real — see
    // isOpenLine/lineContract) even though nothing is actually over-earned:
    // every capped/percent line's own earned is already bounded by its own
    // contract (that is the entire point of the cap), so subtracting only
    // THOSE lines' earned back out of `earned` here always leaves a number
    // >= 0, the same invariant "remaining" has always had.
    remaining: _cents(contract - (earned - openEarned)),
    // Same rule as `remaining`, and for the same reason: an open line adds
    // nothing to `contract` and everything to `earned`, so earned ÷ contract
    // divided money by a total that excludes it (2,157% on a real job with
    // one open line and $3,996 of extras). "% of contract" is the capped/
    // percent portion over the contract — by construction (contract −
    // remaining) ÷ contract, so it can never disagree with "left to earn".
    // What the open lines earned is handed back as `openEarned` so a screen
    // can say it in words beside the percentage instead of folding it into
    // the ratio. A job with no open line has openEarned 0: unchanged.
    // earnedOfContract (below) is that rule, and the only place it is written.
    percent: contract > 0 ? earnedOfContract({ earned, openEarned }, contract).frac * 100 : null,
    openEarned,
    baseContract, extrasContract, unpriced, perLine, alloc,
  };
}

/**
 * "% of contract" — the ONE definition, for every screen that prints it (the
 * Contract strip and its total row, the Job Costs rail, the P&L statement and
 * its "Every job" tile, the P&L PDF, Home's earned cell).
 *
 * An open line earns real money against no contract at all (isOpenLine), so
 * its dollars are taken OUT of the numerator: the ratio is the capped/percent
 * part of the job over the contract — by construction (contract − remaining) ÷
 * contract, so it can never disagree with "left to earn". What the open lines
 * earned comes back beside it as `openEarned`, for the screen to say in words
 * ("+ $82,203.00 earned on open lines") instead of folding it into a ratio
 * whose bottom half excludes it (that division read 2,157% on a real job).
 *
 * `figures` is anything carrying { earned, openEarned } — a projectValue
 * result, a job's summary, a period's figures, a mother's roll-up, a portfolio
 * total — and `contract` is what to measure it against (always job to date:
 * a contract does not window to a period). A figure with no openEarned key
 * reads as 0 open, i.e. plain earned ÷ contract.
 *
 * `frac` is 0-1, null when there is no contract to measure against. Divided in
 * whole cents (every figure handed in is money): fully earned is exactly 1,
 * never 1.000000000000001 from the float subtraction.
 */
export function earnedOfContract(figures, contract) {
  const f = figures || {};
  const cents = (v) => Math.round(num(v) * 100);
  const openEarned = num(f.openEarned);
  const c = cents(contract);
  return { frac: c > 0 ? (cents(f.earned) - cents(openEarned)) / c : null, openEarned };
}

/** Earned minus spent. pct is of earned — margin on nothing is not 0%, it is nothing. */
export function margin(earned, totalSpent) {
  const amount = num(earned) - num(totalSpent);
  return { amount, pct: num(earned) > 0 ? amount / num(earned) * 100 : null };
}

/**
 * Round to 8 decimal places — the precision project_material_rates and
 * material_types.default_revenue_rate store as of migration
 * 20260731070000_widen_revenue_rate_precision.sql. Rounding here, before a
 * derived rate ever reaches an <input>, means what's on screen always
 * matches what a save would write — no separate rounding step to drift out
 * of sync with what the database itself rounds to.
 */
export function roundRate(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 1e8) / 1e8;
}

/**
 * Round to the cent. Money is never shown or stored past 2 decimals. Half away
 * from zero on both sides of zero (see _cents above).
 */
export function roundMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return _cents(n);
}

/**
 * The two-way amount/rate link behind the Contract tab's price inputs (the
 * add-line modal's quantity branch, and the per-row "edit by total"
 * control). The number an admin actually agrees to with a GC is the total;
 * the unit rate is whatever repeating decimal falls out of dividing it by
 * the quantity. Typing either side derives the other from the SAME
 * quantity, so quantity × rate always reconstructs the exact total —
 * never a rate pre-rounded to two decimals and multiplied back out (the
 * bug this pair of functions replaces).
 *
 * Both directions return null — never Infinity or NaN — when the quantity
 * is zero, blank, or otherwise not a positive number: an admin typing an
 * amount before a valid quantity is on screen must see a blank price, not
 * a broken one. Negative inputs are NOT rejected here — that is a save-time
 * concern with its own user-facing message (see 68-jobvalue.js) — so a
 * negative rate or amount still derives its (negative) counterpart for live
 * preview as the admin types.
 *
 * A blank/empty amount or rate ('' — what an <input> reports while empty)
 * also returns null rather than treating it as 0: JS's `Number('')` is `0`,
 * not NaN, so without this an empty rate box would silently derive a real
 * $0.00 amount instead of leaving the other box blank too.
 */
const _numOrNaN = (v) => (v === '' || v == null ? NaN : Number(v));

export function rateFromAmount(amount, qty) {
  const a = _numOrNaN(amount), q = Number(qty);
  if (!Number.isFinite(a) || !Number.isFinite(q) || q <= 0) return null;
  return roundRate(a / q);
}

export function amountFromRate(rate, qty) {
  const r = _numOrNaN(rate), q = Number(qty);
  if (!Number.isFinite(r) || !Number.isFinite(q) || q <= 0) return null;
  return roundMoney(r * q);
}

/**
 * The third leg of the triangle, added for OPEN lines (see isOpenLine
 * above): the add-line modal's Estimated Quantity is now itself optional
 * and derivable, so a rate + a contract total (typed with no quantity on
 * screen yet — "I know the unit price and what we agreed the total is, tell
 * me how many units that implies") has to resolve to a quantity the same
 * way rateFromAmount/amountFromRate already resolve their own third leg.
 *
 * rate <= 0 (or blank/non-finite) returns null the same way qty <= 0 does
 * in the two functions above — there is no meaningful "how many units" when
 * the $/unit itself is unknown or zero, that would be a division by zero.
 * amount is NOT required to be positive (a live preview mid-typing, or a
 * deductive total, still derives a — probably nonsensical but non-crashing
 * — quantity; save-time validation is 68-jobvalue.js's concern, same rule
 * every function in this pair already follows).
 *
 * Rounded to 2 decimals, matching the Estimated Quantity input's own
 * step="0.01" granularity — quantities are physical counts/areas, not
 * currency, so there is no separate "shown rounded, stored exact" shadow
 * here the way the rate box needs one (see rateWasEdited's own doc).
 */
export function qtyFromRateAmount(rate, amount) {
  const r = _numOrNaN(rate), a = _numOrNaN(amount);
  if (!Number.isFinite(r) || r <= 0 || !Number.isFinite(a)) return null;
  return Math.round((a / r) * 100) / 100;
}

/**
 * Whether a rate left in a rounded-for-display input box is a genuine edit,
 * or just the same stored rate reflected back at 2 decimals.
 *
 * The rate box always DISPLAYS the stored rate rounded to 2 decimals (the
 * owner does not want to see "23.77555873" on screen) rather than the up to
 * 8 decimals it is actually stored at. A change handler that writes
 * whatever is in the box on every fire would round away that precision on
 * a save the admin never asked for — a blur, a stray keystroke, a
 * re-render all fire onchange — quietly moving a contract total by dollars
 * on a line nobody touched. Comparing both sides at the SAME 2-decimal
 * rounding is what tells "the admin typed a genuinely new number" apart
 * from "this is the old number, just shown rounded" — the caller skips the
 * write entirely (leaving the precise stored value alone) when this is
 * false.
 *
 * displayedValue is whatever is literally in the box (typically a string
 * from an <input>). storedRate is the exact rate that produced what was
 * shown there — null/undefined means there was no rate on file before.
 */
export function rateWasEdited(displayedValue, storedRate, exact) {
  const typed = _numOrNaN(displayedValue);
  const hadStored = storedRate != null && Number.isFinite(Number(storedRate));
  if (!Number.isFinite(typed)) return hadStored;   // now blank: an edit only if it CLEARED a real rate
  if (!hadStored) return true;                     // nothing on file before: any typed number is new
  // `exact`: the value did NOT come from a 2-decimal display box — it is the
  // full-precision rate a typed TOTAL divides out to (the modals' data-exact
  // shadow). Compared at 2 decimals, a new total that moved the unit price by
  // less than half a cent read as "nothing changed" and was dropped under a
  // "saved" toast — a dead window of quantity × $0.01 ($1,310 wide on a
  // 131,016-unit line). Compared at the precision the column stores, any
  // total that really differs is an edit, and an untouched shadow still is not.
  const round = exact ? roundRate : roundMoney;
  return round(typed) !== round(Number(storedRate));
}

/**
 * The quantity changed in a quantity/price/total triangle: which of the two
 * money boxes follows it. The LAST one the admin typed (`anchor`) is the
 * fact and stays; the other is derived. Returned as { rate, amount } with
 * the kept side passed through untouched, so a caller only repaints the
 * derived box.
 *
 * Without the anchor, "type the new total, then fix the quantity" re-derived
 * the total from a price that had itself just been derived from the new
 * total over the OLD quantity — replacing the number the admin had just
 * typed with a different one.
 */
export function requoteForQty(anchor, rate, amount, qty) {
  return anchor === 'amount'
    ? { rate: rateFromAmount(amount, qty), amount }
    : { rate, amount: amountFromRate(rate, qty) };
}

/**
 * What each BASE quantity line prints as "installed", and which one carries
 * the "beyond approved scope" warning.
 *
 * A line that is the only one on its production item prints the item's raw
 * installed total — that is what makes "1,400 / 1,000" readable as "more was
 * laid than this line covers". When TWO OR MORE base lines share an item
 * (Tower 1 / Tower 2 of the same block) the raw total on every row says more
 * than each line's whole scope is in place while the % beside it — which
 * comes from the allocator's split — says a quarter. So a shared item prints
 * each line's own share, the number its earned figure was computed from.
 *
 * The leftover (alloc.overageByType) is one quantity per item, not per line:
 * it goes on the LAST capped base in drain order — the line that filled
 * last — instead of being repeated on every row. An item with an open base
 * never has leftover, so there is no open case to place.
 */
export function installedShownByLine(lines, alloc, installedByMaterialType) {
  const installed = installedByMaterialType || {};
  const byLine = (alloc && alloc.byLineId) || {};
  const over = (alloc && alloc.overageByType) || {};
  const basesByType = {};
  (lines || []).forEach(l => {
    if (l && l.earn_method === 'quantity' && !l.continues_line_id) {
      (basesByType[l.material_type_id] = basesByType[l.material_type_id] || []).push(l);
    }
  });
  const qtyByLineId = {}, overageByLineId = {};
  Object.entries(basesByType).forEach(([type, group]) => {
    const shared = group.length > 1;
    group.forEach(l => { qtyByLineId[l.id] = shared ? (byLine[l.id] ?? 0) : num(installed[type]); });
    if (!over[type]) return;
    const capped = group.filter(l => !isOpenLine(l)).sort(_baseDrainCmp);
    if (capped.length) overageByLineId[capped[capped.length - 1].id] = over[type];
  });
  return { qtyByLineId, overageByLineId };
}

/**
 * Before saving a new estimated quantity on one base line: how much
 * ALREADY-INSTALLED quantity would stop earning because of the QUANTITY edit.
 *
 * Not `installed − newQty`: that prints a loss that is not there when a
 * second base line or a linked change order catches the overflow. This
 * re-runs the real allocator with the edited line and compares the item's
 * leftover before and after — only the EXTRA leftover is new, and only that
 * is priced (at the price in the modal, the one the line would earn at).
 *
 * BOTH runs use the modal's price. A linked change order holds
 * contract_amount ÷ price units, so the price alone moves the leftover:
 * comparing the stored price against the modal's reported a price-only edit
 * as installed quantity that "stops earning" while earned went UP, and
 * understated the loss when price and quantity both dropped.
 *
 * Null when nothing new stops earning, or when there is no usable price to
 * put a dollar figure on it.
 */
export function qtyEditOverage(lines, ratesByMaterialType, installedByMaterialType, lineId, newQty, newRate) {
  const all = lines || [];
  const line = all.find(l => l && l.id === lineId);
  const r = _numOrNaN(newRate);
  if (!line || line.material_type_id == null || !Number.isFinite(r)) return null;
  const type = line.material_type_id;
  const rr = { ...(ratesByMaterialType || {}), [type]: r };
  const before = allocateInstalled(all, rr, installedByMaterialType).overageByType[type] || 0;
  const after = allocateInstalled(
    all.map(l => (l === line ? { ...l, estimated_qty: newQty } : l)),
    rr, installedByMaterialType).overageByType[type] || 0;
  const qty = _roundQty(after - before);
  return qty > 0 ? { qty, amount: roundMoney(qty * r) } : null;
}
